import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ActivityReportFilters } from "../../../types";

type SummaryMode = "total" | "average";

type ActivityRowFilters = {
  startDate: string;
  endDate: string;
  assignee: string;
  startTime: string;
  endTime: string;
  minDurationHours: string;
  minCost: string;
  projectName: string;
  activityTitle: string;
  formContent: string;
  description: string;
  managerComment: string;
};

type LeaveRowFilters = {
  startDate: string;
  endDate: string;
  assignee: string;
  startTime: string;
  endTime: string;
  minDurationHours: string;
  formContent: string;
  description: string;
};

const INITIAL_ACTIVITY_ROW_FILTERS: ActivityRowFilters = {
  startDate: "",
  endDate: "",
  assignee: "",
  startTime: "",
  endTime: "",
  minDurationHours: "",
  minCost: "",
  projectName: "",
  activityTitle: "",
  formContent: "",
  description: "",
  managerComment: "",
};

const INITIAL_LEAVE_ROW_FILTERS: LeaveRowFilters = {
  startDate: "",
  endDate: "",
  assignee: "",
  startTime: "",
  endTime: "",
  minDurationHours: "",
  formContent: "",
  description: "",
};

const canUseServerSearchPrefilter = (query: string): boolean => {
  const trimmed = query.trim();
  return trimmed.length >= 2 && /[a-zA-ZçğıöşüÇĞİÖŞÜ]/u.test(trimmed);
};

const normalizeFilterText = (value: unknown): string =>
  String(value ?? "").trim().toLocaleLowerCase("tr-TR");

const isKnownLeaveWorkflowName = (value: unknown): boolean =>
  normalizeFilterText(value) === "izin";

export const useActivitiesLeavesTabModel = ({
  mainTab,
  reports,
  users,
  projects,
  activityDefinitions,
  taskActivitiesFilter,
  isManualAssignedTaskActivity,
}: any) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedProject, setSelectedProject] = useState<{
    workflowId: number;
    projectName: string;
  } | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [statusFilterBeforeForcedTab, setStatusFilterBeforeForcedTab] =
    useState<string | null>(null);
  const [workflowFilter, setWorkflowFilter] = useState<string>("all");
  const [activityFilter, setActivityFilter] = useState<string>("all");
  const [assigneeFilter, setAssigneeFilter] = useState<string>("all");
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [activityRowFilters, setActivityRowFilters] =
    useState<ActivityRowFilters>(INITIAL_ACTIVITY_ROW_FILTERS);
  const [activityFormColumnFilters, setActivityFormColumnFilters] = useState<
    Record<string, string>
  >({});
  const [leaveRowFilters, setLeaveRowFilters] = useState<LeaveRowFilters>(
    INITIAL_LEAVE_ROW_FILTERS,
  );
  const [activityCurrentPage, setActivityCurrentPage] = useState(1);
  const [activityItemsPerPage, setActivityItemsPerPage] = useState(1000);
  const [activitySummaryMode, setActivitySummaryMode] =
    useState<SummaryMode>("total");
  const [leaveSummaryMode, setLeaveSummaryMode] =
    useState<SummaryMode>("total");
  const [activitySortField, setActivitySortField] =
    useState<string>("activityCreatedAt");
  const [activitySortOrder, setActivitySortOrder] = useState<
    "asc" | "desc" | null
  >("desc");
  const previousMainTabRef = useRef(mainTab);
  const isActivityLikeTab = mainTab === "activities" || mainTab === "tasks";

  const isLeaveActivityReport = useCallback(
    (report: any): boolean => {
      if (report?.isLeave === true) {
        return true;
      }

      const templateActivityId = report?.templateActivityId;
      if (typeof templateActivityId === "number") {
        const definition = activityDefinitions.find(
          (item: any) => item?.id === templateActivityId,
        );
        if (definition?.isLeave === true) {
          return true;
        }
      }

      const title = (report?.activityTitle || report?.activityInstanceName || "")
        .trim();
      if (!title) {
        return false;
      }

      const definitionByTitle = activityDefinitions.find(
        (item: any) =>
          (item?.title || "").trim() === title && item?.isLeave === true,
      );
      return Boolean(definitionByTitle);
    },
    [activityDefinitions],
  );

  useEffect(() => {
    if (!isActivityLikeTab) return;

    const syncedActivityTitle = activityFilter !== "all" ? activityFilter : "";
    const syncedAssignee = assigneeFilter !== "all" ? assigneeFilter : "";
    const syncedProjectName = selectedProject?.projectName || "";
    const syncedStartDate = startDate || "";
    const syncedEndDate = endDate || "";

    setActivityRowFilters((prev) => {
      if (
        prev.activityTitle === syncedActivityTitle &&
        prev.assignee === syncedAssignee &&
        prev.projectName === syncedProjectName &&
        prev.startDate === syncedStartDate &&
        prev.endDate === syncedEndDate
      ) {
        return prev;
      }

      return {
        ...prev,
        startDate: syncedStartDate,
        endDate: syncedEndDate,
        activityTitle: syncedActivityTitle,
        assignee: syncedAssignee,
        projectName: syncedProjectName,
      };
    });
  }, [
    activityFilter,
    assigneeFilter,
    endDate,
    isActivityLikeTab,
    selectedProject,
    startDate,
  ]);

  useEffect(() => {
    if (mainTab !== "leaves") return;

    const syncedAssignee = assigneeFilter !== "all" ? assigneeFilter : "";
    const syncedStartDate = startDate || "";
    const syncedEndDate = endDate || "";

    setLeaveRowFilters((prev) => {
      if (
        prev.assignee === syncedAssignee &&
        prev.startDate === syncedStartDate &&
        prev.endDate === syncedEndDate
      ) {
        return prev;
      }

      return {
        ...prev,
        assignee: syncedAssignee,
        startDate: syncedStartDate,
        endDate: syncedEndDate,
      };
    });
  }, [assigneeFilter, endDate, mainTab, startDate]);

  useEffect(() => {
    const previousMainTab = previousMainTabRef.current;
    const wasForcedTab =
      previousMainTab === "leaves" || previousMainTab === "tasks";
    const isForcedTab = mainTab === "leaves" || mainTab === "tasks";

    if (isForcedTab && !wasForcedTab && statusFilterBeforeForcedTab === null) {
      setStatusFilterBeforeForcedTab(statusFilter);
    }

    const forcedStatus =
      mainTab === "leaves"
        ? "COMPLETED"
        : mainTab === "tasks"
          ? taskActivitiesFilter
          : null;

    if (forcedStatus && statusFilter !== forcedStatus) {
      previousMainTabRef.current = mainTab;
      setStatusFilter(forcedStatus);
      return;
    }

    if (
      !isForcedTab &&
      wasForcedTab &&
      statusFilterBeforeForcedTab !== null &&
      statusFilter !== statusFilterBeforeForcedTab
    ) {
      previousMainTabRef.current = mainTab;
      setStatusFilter(statusFilterBeforeForcedTab);
      return;
    }

    if (!isForcedTab && wasForcedTab && statusFilterBeforeForcedTab !== null) {
      setStatusFilterBeforeForcedTab(null);
    }

    if (isActivityLikeTab || mainTab === "leaves") {
      setActivityCurrentPage(1);
    }

    previousMainTabRef.current = mainTab;
  }, [
    isActivityLikeTab,
    mainTab,
    statusFilter,
    statusFilterBeforeForcedTab,
    taskActivitiesFilter,
  ]);

  const handleActivitySort = useCallback((field: string) => {
    setActivityCurrentPage(1);
    if (activitySortField === field) {
      if (activitySortOrder === "asc") {
        setActivitySortOrder("desc");
      } else if (activitySortOrder === "desc") {
        setActivitySortOrder(null);
      } else {
        setActivitySortOrder("asc");
      }
      return;
    }

    setActivitySortField(field);
    setActivitySortOrder("asc");
  }, [activitySortField, activitySortOrder]);

  const exactAssigneeIdFilter = useMemo(() => {
    if (assigneeFilter === "all") {
      return undefined;
    }

    const matches = users.filter((candidate: any) => {
      const fullName = `${candidate.firstName || ""} ${candidate.lastName || ""}`.trim();
      return fullName === assigneeFilter;
    });

    return matches.length === 1 ? matches[0].id : undefined;
  }, [assigneeFilter, users]);

  const serverReportFilters = useMemo<ActivityReportFilters | undefined>(() => {
    if (
      mainTab !== "activities" &&
      mainTab !== "tasks" &&
      mainTab !== "leaves" &&
      mainTab !== "projects"
    ) {
      return undefined;
    }

    if (mainTab === "projects") {
      return {};
    }

    const filters: ActivityReportFilters = {};

    if (mainTab === "activities" || mainTab === "tasks") {
      filters.tab = "activities";
      if (selectedProject?.workflowId) {
        filters.workflowInstanceId = selectedProject.workflowId;
      }
      if (workflowFilter !== "all") {
        filters.workflowName = workflowFilter;
      }
      if (activityFilter !== "all") {
        filters.activityTitle = activityFilter;
      }
      if (
        statusFilter !== "all" &&
        statusFilter !== taskActivitiesFilter &&
        statusFilter !== "PENDING"
      ) {
        filters.status = statusFilter;
      }
    }

    if (typeof exactAssigneeIdFilter === "number") {
      filters.assigneeId = exactAssigneeIdFilter;
    }

    if (canUseServerSearchPrefilter(searchQuery)) {
      filters.searchTerm = searchQuery.trim();
    }

    return filters;
  }, [
    activityFilter,
    exactAssigneeIdFilter,
    mainTab,
    searchQuery,
    selectedProject,
    statusFilter,
    taskActivitiesFilter,
    workflowFilter,
  ]);

  const counts = useMemo(() => {
    const reportsToCount = selectedProject
      ? reports.filter(
          (report: any) =>
            report.workflowId === selectedProject.workflowId &&
            report.projectName === selectedProject.projectName,
        )
      : reports;

    return {
      pendingCount: reportsToCount.filter(
        (report: any) =>
          report.activityStatus === "PENDING" ||
          report.activityStatus === "ACTIVE",
      ).length,
      pendingApprovalCount: reportsToCount.filter(
        (report: any) => report.activityStatus === "PENDING_APPROVAL",
      ).length,
      completedCount: reportsToCount.filter(
        (report: any) => report.activityStatus === "COMPLETED",
      ).length,
      rejectedCount: reportsToCount.filter(
        (report: any) => report.activityStatus === "REJECTED",
      ).length,
      taskActivitiesCount: reportsToCount.filter(isManualAssignedTaskActivity)
        .length,
    };
  }, [isManualAssignedTaskActivity, reports, selectedProject]);

  const uniqueAssignees = useMemo(() => {
    const adminNameSet = new Set(
      users
        .filter((userItem: any) => (userItem.role || "").toUpperCase() === "ADMIN")
        .map((userItem: any) =>
          `${userItem.firstName || ""} ${userItem.lastName || ""}`
            .trim()
            .toLocaleLowerCase("tr-TR"),
        )
        .filter(Boolean),
    );

    return users
      .map((userItem: any) =>
        `${userItem.firstName || ""} ${userItem.lastName || ""}`.trim(),
      )
      .filter((rawName: string) => {
        const normalized = (rawName || "").trim().toLocaleLowerCase("tr-TR");
        if (!normalized) return false;
        if (normalized === "multiple users") return false;
        if (normalized === "system administrator") return false;
        if (normalized === "administrator") return false;
        if (adminNameSet.has(normalized)) return false;
        return true;
      })
      .sort((left: string, right: string) => left.localeCompare(right, "tr-TR"));
  }, [users]);

  const uniqueWorkflows = useMemo(
    () =>
      Array.from(
        new Set(
          projects
            .map((project: any) => (project.name || "").trim())
            .filter(Boolean) as string[],
        ),
      ).sort((left, right) => left.localeCompare(right, "tr-TR")),
    [projects],
  );

  const uniqueProjects = useMemo(() => {
    const items = Array.from(
      new Map(
        projects
          .filter((project: any) => {
            const workflowName =
              project.templateWorkflowName || project.name || "";
            if (isKnownLeaveWorkflowName(workflowName)) {
              return false;
            }
            if (workflowFilter !== "all") {
              return workflowName.trim() === workflowFilter;
            }
            return project.id && (project.workflowContainerName || project.name);
          })
          .map((project: any) => [
            `${project.id}-${project.workflowContainerName || project.name}`,
            {
              workflowId: project.id,
              projectName: project.workflowContainerName || project.name,
              workflowName: project.name || "",
            },
          ]),
      ).values(),
    ) as Array<{
      workflowId: number;
      projectName: string;
      workflowName: string;
    }>;

    return items.sort((left, right) =>
      left.projectName.localeCompare(right.projectName),
    );
  }, [projects, workflowFilter]);

  const uniqueActivities = useMemo(() => {
    const reportTitles = Array.from(
      new Set(
        reports
          .filter((report: any) => !isLeaveActivityReport(report))
          .map((report: any) =>
            (report?.activityTitle || report?.activityInstanceName || "").trim(),
          )
          .filter((title: string) => Boolean(title)),
      ),
    ) as string[];

    const items =
      reportTitles.length > 0
        ? reportTitles
        : (Array.from(
            new Set(
              activityDefinitions
                .filter((definition: any) => definition?.isLeave !== true)
                .map((definition: any) => (definition?.title || "").trim())
                .filter((title: string) => Boolean(title)),
            ),
          ) as string[]);

    return items.sort((left, right) => left.localeCompare(right, "tr-TR"));
  }, [activityDefinitions, isLeaveActivityReport, reports]);

  useEffect(() => {
    if (!selectedProject) {
      return;
    }

    const isStillAvailable = uniqueProjects.some(
      (project) =>
        project.workflowId === selectedProject.workflowId &&
        project.projectName === selectedProject.projectName,
    );

    if (!isStillAvailable) {
      setSelectedProject(null);
    }
  }, [selectedProject, uniqueProjects]);

  useEffect(() => {
    if (activityFilter === "all") {
      return;
    }

    if (!uniqueActivities.includes(activityFilter)) {
      setActivityFilter("all");
    }
  }, [activityFilter, uniqueActivities]);

  const clearActivityRowFilters = useCallback(() => {
    setActivityRowFilters(INITIAL_ACTIVITY_ROW_FILTERS);
    setActivityFormColumnFilters({});
  }, []);

  const clearLeaveRowFilters = useCallback(() => {
    setLeaveRowFilters(INITIAL_LEAVE_ROW_FILTERS);
  }, []);

  const clearAllFilters = useCallback(() => {
    setStatusFilter("all");
    setWorkflowFilter("all");
    setActivityFilter("all");
    setAssigneeFilter("all");
    setStartDate("");
    setEndDate("");
    setSearchQuery("");
    setSelectedProject(null);
    clearActivityRowFilters();
    clearLeaveRowFilters();
  }, [clearActivityRowFilters, clearLeaveRowFilters]);

  const hasActiveActivityRowFilters = useMemo(
    () => Object.values(activityRowFilters).some((value) => value.trim() !== ""),
    [activityRowFilters],
  );
  const hasActiveLeaveRowFilters = useMemo(
    () => Object.values(leaveRowFilters).some((value) => value.trim() !== ""),
    [leaveRowFilters],
  );

  const hasActiveFilters =
    mainTab === "leaves"
      ? assigneeFilter !== "all" ||
        startDate !== "" ||
        endDate !== "" ||
        searchQuery !== "" ||
        hasActiveLeaveRowFilters
      : (mainTab !== "tasks" && statusFilter !== "all") ||
        workflowFilter !== "all" ||
        activityFilter !== "all" ||
        assigneeFilter !== "all" ||
        startDate !== "" ||
        endDate !== "" ||
        searchQuery !== "" ||
        selectedProject !== null ||
        hasActiveActivityRowFilters;

  return {
    searchQuery,
    setSearchQuery,
    selectedProject,
    setSelectedProject,
    statusFilter,
    setStatusFilter,
    workflowFilter,
    setWorkflowFilter,
    activityFilter,
    setActivityFilter,
    assigneeFilter,
    setAssigneeFilter,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    activityRowFilters,
    setActivityRowFilters,
    activityFormColumnFilters,
    setActivityFormColumnFilters,
    leaveRowFilters,
    setLeaveRowFilters,
    activityCurrentPage,
    setActivityCurrentPage,
    activityItemsPerPage,
    setActivityItemsPerPage,
    activitySummaryMode,
    setActivitySummaryMode,
    leaveSummaryMode,
    setLeaveSummaryMode,
    activitySortField,
    activitySortOrder,
    handleActivitySort,
    serverReportFilters,
    uniqueAssignees,
    uniqueWorkflows,
    uniqueProjects,
    uniqueActivities,
    clearActivityRowFilters,
    clearLeaveRowFilters,
    clearAllFilters,
    hasActiveFilters,
    ...counts,
  };
};
